import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { reportApiOk, reportApiUnavailable } from './apiStatus';

/**
 * API contract: the SPA and the API share an origin and every backend route
 * lives under `/api`. `VITE_API_URL` may override it with an absolute HTTPS
 * URL that includes `/api` (the production build enforces that).
 */
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const APP_BASE_URL = import.meta.env.VITE_APP_URL ?? '';

/** A request that has not answered after this long fails as a network error. */
export const API_TIMEOUT_MS = 20_000;

/** Wait before the single retry of a GET that never reached the server. */
export const RETRY_BASE_DELAY_MS = 400;
export const RETRY_JITTER_MS = 300;

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT_MS,
  withCredentials: true,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  // axios copies the XSRF cookie into the header only for same-origin URLs
  // unless told to; a split deployment (api.<domain>) needs it explicitly, or
  // every mutating request answers 419 "CSRF token mismatch".
  withXSRFToken: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

/**
 * Fetch the Sanctum CSRF cookie before any mutating request.
 * Call once on app boot (App.tsx) and before login/register.
 */
export async function initCsrf(): Promise<void> {
  const csrfPath = `${APP_BASE_URL}/sanctum/csrf-cookie`.replace(
    /([^:]\/)\/+/g,
    '$1'
  );
  await axios.get(csrfPath, {
    withCredentials: true,
    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',
    withXSRFToken: true,
    timeout: API_TIMEOUT_MS,
  });
}

/** Pages that work signed out: a 401 there must never bounce to sign-in. */
const PUBLIC_PATHS = new Set([
  '/',
  '/login',
  '/auth/login',
  '/auth/register',
  '/auth/forgot-password',
  '/reset-password',
  '/notes',
]);
const PUBLIC_PATH_PREFIXES = ['/help', '/legal'];

export function isPublicPath(pathname: string): boolean {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return (
    PUBLIC_PATHS.has(path) ||
    PUBLIC_PATH_PREFIXES.some(
      prefix => path === prefix || path.startsWith(`${prefix}/`)
    )
  );
}

function isSessionCheckRequest(url: string | undefined): boolean {
  if (!url) return false;
  return url === '/user' || url.endsWith('/user');
}

/** Where an unexpected 401 sends the person. */
export const LOGIN_PATH = '/auth/login';

let redirectingToLogin = false;

/**
 * Redirect to login only for unexpected 401s on protected routes. GET /user
 * during boot is expected to 401 when no session exists. The redirect runs at
 * most once per page load and never from a public page (the login page
 * included), so a 401 cannot loop.
 */
export function shouldRedirectToLogin(
  status: number | undefined,
  requestUrl: string | undefined,
  pathname: string
): boolean {
  return (
    status === 401 &&
    !isSessionCheckRequest(requestUrl) &&
    !isPublicPath(pathname)
  );
}

/** Test hook: the once-per-page guard is module state. */
export function resetLoginRedirectGuard(): void {
  redirectingToLogin = false;
}

type RetryableConfig = InternalAxiosRequestConfig & { hcRetried?: boolean };

/**
 * A request that is safe to send twice: a GET or HEAD that got no response at
 * all (connection dropped, DNS, offline blip). Timeouts are not retried (the
 * server may be slow, and a second 20 s wait helps nobody), nor cancelled
 * requests, nor anything with a response, nor a device that is offline.
 */
export function isRetryableNetworkFailure(
  error: unknown,
  config: Pick<AxiosRequestConfig, 'method'> & { hcRetried?: boolean } = {}
): boolean {
  const axiosError = error as Partial<AxiosError> | null;
  if (!axiosError || axiosError.response || config.hcRetried) return false;
  const method = (config.method ?? 'get').toLowerCase();
  if (method !== 'get' && method !== 'head') return false;
  const code = axiosError.code;
  if (
    code === 'ECONNABORTED' ||
    code === 'ETIMEDOUT' ||
    code === 'ERR_CANCELED'
  ) {
    return false;
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return false;
  }
  return true;
}

export function retryDelayMs(random: () => number = Math.random): number {
  return RETRY_BASE_DELAY_MS + Math.floor(random() * RETRY_JITTER_MS);
}

api.interceptors.response.use(
  response => {
    reportApiOk();
    return response;
  },
  async error => {
    const config = error?.config as RetryableConfig | undefined;

    if (config && isRetryableNetworkFailure(error, config)) {
      config.hcRetried = true;
      await new Promise<void>(resolve => setTimeout(resolve, retryDelayMs()));
      return api.request(config);
    }

    const status: number | undefined = error?.response?.status;

    if (status === 503) {
      reportApiUnavailable();
    } else if (error?.response) {
      // Any answer other than 503 means the service is up.
      reportApiOk();
    }

    if (
      typeof window !== 'undefined' &&
      !redirectingToLogin &&
      shouldRedirectToLogin(
        status,
        config?.url as string | undefined,
        window.location.pathname
      )
    ) {
      redirectingToLogin = true;
      window.location.href = LOGIN_PATH;
    }
    return Promise.reject(error);
  }
);

export default api;
